// src/Informes/InformeGeneral.js
import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import "./Scss/InformesGStyles.scss";
//   Imports desde nuestros módulos
import {
  columnsTableContacts,
  columnsTableClients,
  columnsTableAsocZone,
} from "./Components/Tables";
import Pagination from "./Components/Pagination";
import { CasesLineChart, CasesPieCharts } from "./Components/Charts";
import { getRandomColor } from "./Components/helpers";
import { useUser } from "../Components/Context/UserProvider";
import { CommonSubHeader } from "../Components/helpers/Helpers";
import Table from "../Components/Common/Table/Table";
import LoadingPage from "../Components/Common/Loading/LoadingPage";
import { Tabs, Tab, Box, Fade, Grid } from "@mui/material";
import api from "../api/axios";

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function InformeGeneral() {
  const [value, setValue] = useState(0);

  // =====================
  // CONTEXTO DE USUARIO
  // =====================
  const { userData } = useUser();
  const { user } = userData;

  // =====================
  // ESTADOS
  // =====================
  const [contactsData, setContactsData] = useState([]);
  const [clientsData, setClientsData] = useState([]);
  const [asocZoneData, setAsocZoneData] = useState([]);
  const [casesGraph, setCasesGraph] = useState([{ date: "", count: 0 }]);
  const [casesStatusData, setCasesStatusData] = useState([]);
  const [otherCasesStatus, setOtherCasesStatus] = useState([]);

  const [company] = useState({ company_name: "Seleccionar", id: 0 });

  const [pagination, setPagination] = useState({
    contacts: { page: 1, totalPages: 1, pageCount: 0 },
    clients: { page: 1, totalPages: 1, pageCount: 0 },
    asocZone: { page: 1, totalPages: 1, pageCount: 0 },
  });

  const [loading, setLoading] = useState({
    contacts: false,
    clients: false,
    asocZone: false,
    cases: false,
  });

  // =====================
  // FECHAS (Memoizadas)
  // =====================
  const { dateFrom, dateTo } = useMemo(() => {
    const now = dayjs();
    return {
      dateFrom: now.subtract(3, "month").format("YYYY-MM-DD HH:mm:ss"),
      dateTo: now.format("YYYY-MM-DD HH:mm:ss"),
    };
  }, []);

  // =====================
  // HELPER PARA FETCH
  // =====================
  const fetchData = async (
    url,
    page,
    setter,
    loadingKey,
    paginationKey = null,
    controller,
  ) => {
    const formData = {
      page,
      dateFrom,
      dateTo,
      company: company?.id !== 0 ? company?.id : 1,
    };

    try {
      // Activamos el loading solo si no fue abortado
      if (!controller.signal.aborted) {
        setLoading((prev) => ({ ...prev, [loadingKey]: true }));
      }

      const res = await api.post(url, formData, {
        signal: controller.signal,
      });

      console.log(res);

      if (!controller.signal.aborted) {
        let paginated = null;

        if (res?.data?.CasesByStatus || res?.data?.GraphCases) {
          paginated = res?.data;
        } else {
          paginated = res?.data?.data;
        }

        //  Solo setea si los datos existen
        if (paginated?.data) {
          setter(paginated.data);
        }

        //  Actualiza la paginación solo si los datos existen
        if (paginationKey && paginated?.last_page) {
          setPagination((prev) => ({
            ...prev,
            [paginationKey]: {
              ...prev[paginationKey],
              totalPages: paginated?.total ?? prev[paginationKey].totalPages,
              pageCount: paginated?.last_page ?? prev[paginationKey].pageCount,
              currentPage:
                paginated?.current_page ?? prev[paginationKey].currentPage,
            },
          }));
        }
      }
    } catch (err) {
      if (err.name !== "CanceledError") {
        console.error(`   Error fetching ${url}`, err);

        // Si hubo error, limpiar solo si es seguro
        if (!controller.signal.aborted) {
          setter([]);
        }
      }
    } finally {
      // Desactivar loading solo si no fue abortado
      if (!controller.signal.aborted) {
        setLoading((prev) => ({ ...prev, [loadingKey]: false }));
      }
    }
  };

  // Nueva función con polling
  const fetchCasesByStatus = async (retryCount = 0) => {
    const controller = new AbortController();

    const formData = {
      dateFrom,
      dateTo,
      company: company?.id !== 0 ? company?.id : 1,
    };

    if (!controller.signal.aborted) {
      setLoading((prev) => ({ ...prev, ["cases"]: true }));
    }

    try {
      if (!controller.signal.aborted) {
        const res = await api.post(
          `${process.env.REACT_APP_API_URL}/casesbystatus`,
          formData,
          {
            signal: controller.signal,
          },
        );

        console.log("Respuesta API (cruda):", res);

        const apiData = res.data; // 👈 Aquí está el JSON de tu backend

        // Caso: todavía procesando
        if (apiData?.status === "processing" && retryCount < 15) {
          console.log(
            `⌛ Datos en proceso (intento ${
              retryCount + 1
            }), reintentando en 3s...`,
          );
          setTimeout(() => fetchCasesByStatus(retryCount + 1), 1000);
          return;
        }

        // Caso: ya tenemos datos reales
        const casesStatus = apiData?.data;
        if (!casesStatus || !Array.isArray(casesStatus)) {
          console.warn("⚠️ No se encontraron datos aún.");
          return;
        }

        // === Tu lógica original ===
        const totalCases = casesStatus.reduce(
          (acc, { value }) => acc + value,
          0,
        );

        let casesStatusDataWithPercentages = casesStatus.map((item) => ({
          ...item,
          value: ((item.value / totalCases) * 100).toFixed(2),
          label: `${item.case_type_name} ${item.full_case_status_name}`,
        }));

        const threshold = 1;
        const others = casesStatusDataWithPercentages.filter(
          ({ value }) => parseFloat(value) < threshold,
        );

        if (others.length > 0) {
          const sumOthers = others
            .reduce((acc, { value }) => acc + parseFloat(value), 0)
            .toFixed(2);

          casesStatusDataWithPercentages = [
            ...casesStatusDataWithPercentages.filter(
              ({ value }) => parseFloat(value) >= threshold,
            ),
            { label: "Otros", value: sumOthers },
          ];
        }

        const assignColors = (items) =>
          items.map((item, i) => ({
            ...item,
            color: `hsl(${(i * 360) / items.length}, 70%, 50%)`,
          }));

        console.log(
          " Datos finales:",
          assignColors(casesStatusDataWithPercentages),
        );

        setOtherCasesStatus(assignColors(others));
        setCasesStatusData(assignColors(casesStatusDataWithPercentages));
      }
    } catch (error) {
      console.error("Error al obtener casos:", error);
    }

    return () => {
      controller.abort();
    };
  };

  // =====================
  // USEEFFECTS
  // =====================
  // Contactos
  useEffect(() => {
    if (!user) return;
    const controller = new AbortController();

    fetchData(
      `${process.env.REACT_APP_API_URL}/contactstable`,
      pagination.contacts.page,
      setContactsData,
      "contacts",
      "contacts",
      controller,
    );

    return () => controller.abort();
  }, [user, pagination.contacts.page, dateFrom, dateTo, company]);

  // Clientes
  useEffect(() => {
    if (!user) return;
    const controller = new AbortController();

    fetchData(
      `${process.env.REACT_APP_API_URL}/clientsmonthasoc`,
      pagination.clients.page,
      setClientsData,
      "clients",
      "clients",
      controller,
    );

    return () => controller.abort();
  }, [user, pagination.clients.page, dateFrom, dateTo, company]);

  // Casos por mes y status
  useEffect(() => {
    if (!user) return;

    const controllerGraph = new AbortController();

    fetchData(
      `${process.env.REACT_APP_API_URL}/graphcases`,
      null,
      (data) => {
        const normalized = data.map((item) => ({
          date: item.date ?? "",
          count: item.count ?? 0,
        }));
        setCasesGraph(normalized);
      },
      "cases",
      null,
      controllerGraph,
    );

    return () => {
      controllerGraph.abort();
    };
  }, [user, dateFrom, dateTo, company]);

  useEffect(() => {
    if (!user) return;
    fetchCasesByStatus(0);
  }, [user, dateFrom, dateTo, company]);

  // Asoc. y Zona
  useEffect(() => {
    if (!user) return;
    const controller = new AbortController();

    fetchData(
      `${process.env.REACT_APP_API_URL}/casesasoczone?page=${pagination.asocZone.page}`,
      pagination.asocZone.page,
      setAsocZoneData,
      "asocZone",
      "asocZone",
      controller,
    );

    return () => controller.abort();
  }, [user, pagination.asocZone.page, dateFrom, dateTo, company]);

  // =====================
  // TABS HANDLE
  // =====================
  const handleChange = (event, newValue) => setValue(newValue);

  // =====================
  // RENDER
  // =====================
  return (
    <div className="fatherContainer">
      {CommonSubHeader("Informe General")}
      <CommonSubHeader title="Informe General" />

      <div className="mainContainer">
        <div className="subMainContainer">
          <Box sx={{ width: "100%", marginTop: "20px" }}>
            <Tabs
              value={value}
              onChange={handleChange}
              centered
              textColor="primary"
              indicatorColor="primary"
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: "bold",
                  fontSize: "16px",
                  color: "#555",
                  transition: "all 0.3s ease",
                },
                "& .MuiTab-root.Mui-selected": { color: "#1976d2" },
              }}>
              <Tab label="INFORME CONTACTOS" />
              <Tab label="INFORME CLIENTES" />
              <Tab label="INFORME EXPEDIENTES" />
            </Tabs>
            <TabPanel value={value} index={0}>
              {loading?.contacts ? (
                <LoadingPage isLoading={loading.contacts} heightProp="60vh" />
              ) : (
                <Fade timeout={600} in={true}>
                  <div className="dashboardContainers">
                    <Table
                      columns={columnsTableContacts}
                      data={contactsData}
                      viewTitle="informe-expediente"
                      totalPagesNumber={pagination.contacts.totalPages}
                    />
                    {pagination.contacts.totalPages > 0 && (
                      <div className="tablePaginateContainer">
                        <div className="paginatorLeftText">
                          Mostrando {10 * (pagination.contacts.page - 1) + 1} a{" "}
                          {Math.min(
                            10 * pagination.contacts.page,
                            pagination.contacts.totalPages,
                          )}{" "}
                          de {pagination.contacts.totalPages} resultados
                        </div>
                        <Pagination
                          pageCount={pagination.contacts.pageCount}
                          onPageChange={(e) =>
                            setPagination((prev) => ({
                              ...prev,
                              contacts: {
                                ...prev.contacts,
                                page: e.selected + 1,
                              },
                            }))
                          }
                        />
                      </div>
                    )}
                  </div>
                </Fade>
              )}
            </TabPanel>

            <TabPanel value={value} index={1}>
              {loading?.clients ? (
                <LoadingPage isLoading={loading.clients} heightProp="60vh" />
              ) : (
                <Fade timeout={600} in={clientsData.length > 0}>
                  <div className="dashboardContainers">
                    <Table
                      columns={columnsTableClients}
                      data={clientsData}
                      viewTitle="informe-expediente"
                      totalPagesNumber={pagination.clients.totalPages}
                    />
                    {pagination.clients.totalPages > 1 && (
                      <div className="tablePaginateContainer">
                        <div className="paginatorLeftText">
                          Mostrando {10 * (pagination.clients.page - 1) + 1} a{" "}
                          {Math.min(
                            10 * pagination.clients.page,
                            pagination.clients.totalPages,
                          )}{" "}
                          de {pagination.clients.totalPages} resultados
                        </div>
                        <Pagination
                          pageCount={pagination.clients.totalPages}
                          onPageChange={(e) =>
                            setPagination((prev) => ({
                              ...prev,
                              clients: {
                                ...prev.clients,
                                page: e.selected + 1,
                              },
                            }))
                          }
                        />
                      </div>
                    )}
                  </div>
                </Fade>
              )}
            </TabPanel>

            <TabPanel value={value} index={2}>
              {loading?.asocZone ? (
                <LoadingPage isLoading={loading.asocZone} heightProp="60vh" />
              ) : (
                <Grid container>
                  <Grid
                    container
                    item
                    xs={7}
                    padding="10px"
                    height={"fit-content"}>
                    <Fade timeout={600} in={asocZoneData.length > 0}>
                      <div
                        style={{ width: "100%" }}
                        className="dashboardContainers">
                        <Table
                          columns={columnsTableAsocZone}
                          data={asocZoneData}
                          viewTitle="informe-expediente"
                          totalPagesNumber={pagination.asocZone.totalPages}
                        />
                        {pagination.asocZone.totalPages > 0 && (
                          <div className="tablePaginateContainer">
                            <div className="paginatorLeftText">
                              Mostrando{" "}
                              {10 * (pagination.asocZone.page - 1) + 1} a{" "}
                              {Math.min(
                                10 * pagination.asocZone.page,
                                pagination.asocZone.totalPages,
                              )}{" "}
                              de {pagination.asocZone.totalPages} resultados
                            </div>
                            <Pagination
                              pageCount={pagination.asocZone.pageCount}
                              onPageChange={(e) =>
                                setPagination((prev) => ({
                                  ...prev,
                                  asocZone: {
                                    ...prev.asocZone,
                                    page: e.selected + 1,
                                  },
                                }))
                              }
                            />
                          </div>
                        )}
                      </div>
                    </Fade>
                  </Grid>
                  <Grid container item xs={5}>
                    {casesGraph.length > 0 && (
                      <CasesLineChart casesGraph={casesGraph} />
                    )}
                    {casesStatusData.length > 0 && (
                      <CasesPieCharts
                        casesStatusData={casesStatusData}
                        flag6={loading.cases}
                      />
                    )}
                  </Grid>
                </Grid>
              )}
            </TabPanel>
          </Box>
        </div>
      </div>
    </div>
  );
}
